import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  CalendarClock,
  Clock,
  MapPin,
  User,
  Check,
  CalendarDays,
  X,
} from 'lucide-react';

import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { listShowings, shouldUseApi, updateShowing } from '../services/api.js';
import {
  showings as seedShowings,
  leadById,
  propertyById,
  agentById,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);

// ── status metadata ──────────────────────────────────────────────────────────
// Left-border color per status.
const STATUS_BORDER = {
  confirmed: '#22c55e',
  scheduled: '#3b82f6',
  needs_confirmation: '#f59e0b',
  completed: '#22c55e',
  cancelled: '#6b7280',
  no_show: '#ef4444',
};

const STATUS_BADGE = {
  confirmed: { label: 'Confirmed', cls: 'bg-[#DCFCE7] text-[#16A34A]' },
  scheduled: { label: 'Scheduled', cls: 'bg-[#DBEAFE] text-[#2563EB]' },
  needs_confirmation: { label: 'Needs confirmation', cls: 'bg-[#FEF3C7] text-[#D97706]' },
  completed: { label: 'Completed', cls: 'bg-[#DCFCE7] text-[#16A34A]' },
  cancelled: { label: 'Cancelled', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  no_show: { label: 'No show', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
};

function statusBadge(status) {
  return STATUS_BADGE[status] || { label: status, cls: 'bg-[#F3F4F6] text-[#6B7280]' };
}

function statusBorder(status) {
  return STATUS_BORDER[status] || '#6b7280';
}

function confirmationLabel(s) {
  if (s.status === 'confirmed') {
    return `Confirmed ${relativeTime(s.confirmedAt)}`;
  }
  if (s.status === 'needs_confirmation') return 'Awaiting client confirmation';
  if (s.status === 'scheduled') return 'Not yet confirmed';
  if (s.status === 'cancelled') return 'Cancelled';
  if (s.status === 'no_show') return 'Lead did not show';
  if (s.status === 'completed') return 'Completed';
  return '—';
}

// ── time helpers ─────────────────────────────────────────────────────────────

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShortDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diffMin = Math.round((NOW - then) / 60_000);
  if (diffMin < 0) {
    const ahead = -diffMin;
    if (ahead < 60) return `in ${ahead}m`;
    return `in ${Math.round(ahead / 60)}h`;
  }
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'yesterday';
  return `${diffD}d ago`;
}

// Format an ISO string into a value the datetime-local input expects:
// "YYYY-MM-DDTHH:mm" in the user's local time zone.
function isoToLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const tzOffsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

function localInputValueToIso(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

// ── sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const meta = statusBadge(status);
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function OutlinedAction({
  children,
  onClick,
  disabled,
  'aria-label': ariaLabel,
  color = 'neutral',
}) {
  const palette =
    color === 'success'
      ? { border: '#16A34A', text: '#16A34A', hover: 'rgba(22,163,74,0.10)' }
      : color === 'danger'
        ? { border: '#DC2626', text: '#DC2626', hover: 'rgba(220,38,38,0.10)' }
        : color === 'accent'
          ? { border: '#EA580C', text: '#EA580C', hover: 'rgba(234,88,12,0.10)' }
          : { border: '#9CA3AF', text: '#6B7280', hover: 'rgba(107,114,128,0.10)' };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{ borderColor: palette.border, color: palette.text, borderWidth: '1.5px' }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = palette.hover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                 px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap
                 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function ModalShell({ title, onClose, children, footer }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        ref={ref}
        tabIndex={-1}
        className="card w-full max-w-md p-6 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

function RescheduleModal({ showing, onCancel, onConfirm, saving }) {
  const [value, setValue] = useState(() => isoToLocalInputValue(showing.scheduledAt));
  const canSave = Boolean(value) && !saving;
  return (
    <ModalShell
      title="Reschedule showing"
      onClose={saving ? () => {} : onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center rounded-btn border border-border-card
                       px-4 py-2 text-sm text-text-primary hover:border-accent
                       disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canSave && onConfirm(localInputValueToIso(value))}
            disabled={!canSave}
            className="inline-flex items-center rounded-btn bg-accent text-white
                       px-4 py-2 text-sm font-semibold hover:bg-accent-hover
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving…' : 'Save new time'}
          </button>
        </>
      }
    >
      <p className="text-sm text-text-secondary">
        Pick a new date and time for this showing.
      </p>
      <label className="block space-y-1.5">
        <span className="text-xs uppercase tracking-wider text-text-secondary">
          New scheduled time
        </span>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full bg-white border border-border-card rounded-btn text-text-primary
                     text-sm px-3 py-2 hover:border-accent/60 focus:border-accent
                     focus:outline-none focus:ring-1 focus:ring-accent/50 transition-colors"
        />
      </label>
    </ModalShell>
  );
}

function ConfirmDialog({ title, message, confirmLabel, onCancel, onConfirm, busy, danger }) {
  return (
    <ModalShell
      title={title}
      onClose={busy ? () => {} : onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center rounded-btn border border-border-card
                       px-4 py-2 text-sm text-text-primary hover:border-accent
                       disabled:opacity-50 transition-colors"
          >
            Keep showing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center rounded-btn px-4 py-2 text-sm font-semibold
                       text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                         danger
                           ? 'bg-[#DC2626] hover:bg-[#B91C1C]'
                           : 'bg-accent hover:bg-accent-hover'
                       }`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-text-primary">{message}</p>
    </ModalShell>
  );
}

function TodayCard({ showing }) {
  const lead = leadById[showing.leadId] || showing.lead || null;
  const property =
    (showing.propertyId && propertyById[showing.propertyId]) ||
    showing.property ||
    null;
  const agent = (showing.agentId && agentById[showing.agentId]) || showing.agent || null;
  return (
    <article
      className="card p-6 md:px-7 md:py-6 h-full flex flex-col"
      style={{ borderLeft: `4px solid ${statusBorder(showing.status)}` }}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between h-full">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {lead ? (
              <Link
                to={`/leads/${lead.id}`}
                className="text-lg font-semibold text-text-primary hover:text-accent truncate"
              >
                {lead.name}
              </Link>
            ) : (
              <span className="text-lg font-semibold text-text-primary">Unknown lead</span>
            )}
            <StatusBadge status={showing.status} />
          </div>
          <div className="flex items-start gap-2 text-sm text-text-secondary">
            <MapPin size={14} className="mt-0.5 shrink-0" />
            <span className="text-text-primary">
              {property ? property.address : 'Property TBD'}
              {property?.neighborhood && (
                <span className="text-text-secondary">
                  {' · '}
                  {property.neighborhood}
                </span>
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} />
              <span className="text-text-primary font-medium">{formatTime(showing.scheduledAt)}</span>
              {showing.durationMinutes && (
                <span className="text-text-secondary">· {showing.durationMinutes}m</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <User size={14} />
              {agent ? agent.name : 'Unassigned'}
            </span>
          </div>
          {showing.notes && (
            <p className="text-sm text-text-secondary pt-1">{showing.notes}</p>
          )}
        </div>
        <div className="md:text-right space-y-1 shrink-0">
          <p className="text-xs uppercase tracking-wider text-text-secondary">
            Confirmation
          </p>
          <p className="text-sm text-text-primary">{confirmationLabel(showing)}</p>
        </div>
      </div>
    </article>
  );
}

function UpcomingRow({ showing, busy, onConfirm, onReschedule, onCancel }) {
  const lead = leadById[showing.leadId] || showing.lead || null;
  const property =
    (showing.propertyId && propertyById[showing.propertyId]) ||
    showing.property ||
    null;
  const agent = (showing.agentId && agentById[showing.agentId]) || showing.agent || null;
  const isCancelled = showing.status === 'cancelled';
  const isConfirmed = showing.status === 'confirmed';
  return (
    <tr
      className="border-t border-border-card hover:bg-bg-subtle transition-colors"
      style={{ borderLeft: `4px solid ${statusBorder(showing.status)}` }}
    >
      <td className="px-4 py-3 whitespace-nowrap text-text-primary">
        {formatShortDate(showing.scheduledAt)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
        {formatTime(showing.scheduledAt)}
      </td>
      <td className="px-4 py-3 min-w-[160px]">
        {lead ? (
          <Link
            to={`/leads/${lead.id}`}
            className="font-medium text-text-primary hover:text-accent"
          >
            {lead.name}
          </Link>
        ) : (
          <span className="text-text-secondary italic">Unknown lead</span>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary max-w-[260px]">
        <div className="truncate" title={property?.address || ''}>
          {property ? property.address : 'Property TBD'}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-text-primary">
        {agent ? agent.name : <span className="text-hot italic">Unassigned</span>}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={showing.status} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <OutlinedAction
            aria-label="Confirm showing"
            color="success"
            disabled={busy || isConfirmed || isCancelled}
            onClick={() => onConfirm(showing)}
          >
            <Check size={12} /> Confirm
          </OutlinedAction>
          <OutlinedAction
            aria-label="Reschedule showing"
            color="accent"
            disabled={busy || isCancelled}
            onClick={() => onReschedule(showing)}
          >
            <CalendarDays size={12} /> Reschedule
          </OutlinedAction>
          <OutlinedAction
            aria-label="Cancel showing"
            color="danger"
            disabled={busy || isCancelled}
            onClick={() => onCancel(showing)}
          >
            <X size={12} /> Cancel
          </OutlinedAction>
        </div>
      </td>
    </tr>
  );
}

function PastRow({ showing }) {
  const lead = leadById[showing.leadId] || showing.lead || null;
  const property =
    (showing.propertyId && propertyById[showing.propertyId]) ||
    showing.property ||
    null;
  const agent = (showing.agentId && agentById[showing.agentId]) || showing.agent || null;
  return (
    <tr
      className="border-t border-border-card hover:bg-bg-subtle transition-colors"
      style={{ borderLeft: `4px solid ${statusBorder(showing.status)}` }}
    >
      <td className="px-4 py-3 whitespace-nowrap text-text-primary">
        {formatShortDate(showing.scheduledAt)}
      </td>
      <td className="px-4 py-3 min-w-[160px]">
        {lead ? (
          <Link
            to={`/leads/${lead.id}`}
            className="font-medium text-text-primary hover:text-accent"
          >
            {lead.name}
          </Link>
        ) : (
          <span className="text-text-secondary italic">Unknown lead</span>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary max-w-[240px]">
        <div className="truncate" title={property?.address || ''}>
          {property ? property.address : 'Property TBD'}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-text-primary">
        {agent ? agent.name : '—'}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={showing.status} />
      </td>
      <td className="px-4 py-3 text-text-secondary max-w-[320px]">
        <div className="truncate" title={showing.notes || ''}>
          {showing.notes || '—'}
        </div>
      </td>
    </tr>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, count }) {
  return (
    <header className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-btn flex items-center justify-center bg-accent/10 text-accent">
          <Icon size={18} />
        </div>
        <div>
          <h2 className="section-header mb-0">{title}</h2>
          {subtitle && <p className="text-xs text-text-secondary">{subtitle}</p>}
        </div>
      </div>
      <span className="badge bg-bg-subtle border border-border-card text-text-secondary tabular-nums">
        {count}
      </span>
    </header>
  );
}

function EmptyCard({ children }) {
  return (
    <div
      className="card p-8 text-center text-sm text-text-secondary italic flex items-center justify-center"
      style={{ minHeight: '160px' }}
    >
      {children}
    </div>
  );
}

// Shimmer placeholder used while the initial fetch is in flight.  Kept at a
// fixed height so the surrounding section never collapses and then re-expands
// when data arrives — that's what was making the page "disappear and come
// back" between renders.
function ShowingsSkeleton({ variant = 'cards' }) {
  if (variant === 'cards') {
    return (
      <div
        className="grid gap-6 items-stretch"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="card p-6"
            style={{ minHeight: '180px' }}
            aria-hidden="true"
          >
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-2/3 rounded bg-bg-subtle" />
              <div className="h-3 w-1/2 rounded bg-bg-subtle" />
              <div className="h-3 w-1/3 rounded bg-bg-subtle" />
              <div className="h-3 w-3/4 rounded bg-bg-subtle" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="card p-6" style={{ minHeight: '200px' }} aria-hidden="true">
      <div className="animate-pulse space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-4 w-full rounded bg-bg-subtle" />
        ))}
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

const PAST_STATUSES = new Set(['completed', 'cancelled', 'no_show']);

export default function Showings() {
  const { showToast } = useToast();
  const showingsReq = useApiData(() => listShowings({ limit: 500 }), []);

  // Merge any local updates on top of the fetched list so buttons reflect
  // their new state even before a refetch.
  const [overrides, setOverrides] = useState({});

  // "Live mode" means the API gate is on — a real user with a JWT, not a
  // demo session.  In live mode we trust the API response (including an
  // empty list) and never paper over it with seed data.  In demo mode the
  // hook skips the fetch and ``data`` stays null, so we fall back to seeds.
  const isLive = shouldUseApi();
  const isInitialLoading =
    isLive && showingsReq.loading && !showingsReq.data;

  const baseShowings = isLive
    ? (showingsReq.data?.showings ?? [])
    : seedShowings;

  const showings = useMemo(
    () =>
      baseShowings.map((s) =>
        overrides[s.id] ? { ...s, ...overrides[s.id] } : s,
      ),
    [baseShowings, overrides],
  );

  const [busyId, setBusyId] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const applyUpdate = (id, patch) => {
    setOverrides((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const handleConfirm = async (showing) => {
    setBusyId(showing.id);
    try {
      const updated = await updateShowing(showing.id, { status: 'confirmed' });
      applyUpdate(showing.id, {
        status: 'confirmed',
        confirmedAt: updated.confirmedAt || new Date().toISOString(),
      });
      showToast('Showing confirmed', { type: 'success' });
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not confirm showing', {
        type: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const doCancel = async () => {
    if (!cancelTarget) return;
    const target = cancelTarget;
    setBusyId(target.id);
    try {
      await updateShowing(target.id, { status: 'cancelled' });
      applyUpdate(target.id, { status: 'cancelled' });
      setCancelTarget(null);
      showToast('Showing cancelled', { type: 'success' });
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not cancel showing', {
        type: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const doReschedule = async (newIso) => {
    if (!rescheduleTarget) return;
    const target = rescheduleTarget;
    setBusyId(target.id);
    try {
      await updateShowing(target.id, { scheduled_at: newIso });
      applyUpdate(target.id, { scheduledAt: newIso });
      setRescheduleTarget(null);
      showToast(`Showing rescheduled to ${formatDateTime(newIso)}`, {
        type: 'success',
      });
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not reschedule showing', {
        type: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const { today, upcoming, past } = useMemo(() => {
    const todayList = [];
    const upcomingList = [];
    const pastList = [];

    for (const s of showings) {
      const when = new Date(s.scheduledAt);
      if (PAST_STATUSES.has(s.status) || when < NOW) {
        pastList.push(s);
      } else if (sameDay(when, NOW)) {
        todayList.push(s);
      } else {
        upcomingList.push(s);
      }
    }

    todayList.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    upcomingList.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    pastList.sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));

    return { today: todayList, upcoming: upcomingList, past: pastList };
  }, [showings]);

  return (
    <div className="space-y-8" style={{ minHeight: '900px' }}>
      <header>
        <div className="flex items-center gap-3">
          <h1 className="page-title mb-0">Showings</h1>
          {showingsReq.loading && <InlineSpinner />}
        </div>
        <p className="page-subtitle mb-0 mt-2">
          {formatDate(NOW_ISO)} · {today.length} today, {upcoming.length} upcoming, {past.length} past.
        </p>
      </header>

      {showingsReq.error && (
        <ErrorBanner
          message="Couldn’t load showings from the server."
          onRetry={showingsReq.refetch}
        />
      )}

      {/* Today's Showings */}
      <section className="space-y-4" style={{ minHeight: '280px' }}>
        <SectionHeader
          icon={CalendarCheck}
          title="Today's Showings"
          subtitle="Every tour on the calendar today, in order."
          count={isInitialLoading ? 0 : today.length}
        />
        {isInitialLoading ? (
          <ShowingsSkeleton variant="cards" />
        ) : today.length === 0 ? (
          <EmptyCard>No showings on the calendar today.</EmptyCard>
        ) : (
          <div
            className="grid gap-6 items-stretch"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}
          >
            {today.map((s) => (
              <TodayCard key={s.id} showing={s} />
            ))}
          </div>
        )}
      </section>

      {/* Upcoming */}
      <section className="space-y-4" style={{ minHeight: '280px' }}>
        <SectionHeader
          icon={CalendarClock}
          title="Upcoming Showings"
          subtitle="Future tours across the pipeline."
          count={isInitialLoading ? 0 : upcoming.length}
        />
        {isInitialLoading ? (
          <ShowingsSkeleton variant="table" />
        ) : upcoming.length === 0 ? (
          <EmptyCard>Nothing scheduled beyond today.</EmptyCard>
        ) : (
          <div className="card overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="w-full" style={{ fontSize: '15px' }}>
                <thead className="bg-bg-subtle border-b border-border-card">
                  <tr>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Date
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Time
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Lead
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Property
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Agent
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Status
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.map((s) => (
                    <UpcomingRow
                      key={s.id}
                      showing={s}
                      busy={busyId === s.id}
                      onConfirm={handleConfirm}
                      onReschedule={(target) => setRescheduleTarget(target)}
                      onCancel={(target) => setCancelTarget(target)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Past */}
      <section className="space-y-4" style={{ minHeight: '280px' }}>
        <SectionHeader
          icon={Clock}
          title="Past Showings"
          subtitle="Completed, cancelled, and no-show tours."
          count={isInitialLoading ? 0 : past.length}
        />
        {isInitialLoading ? (
          <ShowingsSkeleton variant="table" />
        ) : past.length === 0 ? (
          <EmptyCard>No past showings on record.</EmptyCard>
        ) : (
          <div className="card overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="w-full" style={{ fontSize: '15px' }}>
                <thead className="bg-bg-subtle border-b border-border-card">
                  <tr>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Date
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Lead
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Property
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Agent
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Outcome
                    </th>
                    <th style={{ fontSize: '14px' }} className="px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {past.map((s) => (
                    <PastRow key={s.id} showing={s} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {rescheduleTarget && (
        <RescheduleModal
          showing={rescheduleTarget}
          saving={busyId === rescheduleTarget.id}
          onCancel={() => setRescheduleTarget(null)}
          onConfirm={doReschedule}
        />
      )}
      {cancelTarget && (
        <ConfirmDialog
          title="Cancel this showing?"
          message={`This will cancel ${leadById[cancelTarget.leadId]?.name || 'the lead'}'s showing on ${formatDateTime(cancelTarget.scheduledAt)}. The lead can be rebooked later.`}
          confirmLabel="Cancel showing"
          danger
          busy={busyId === cancelTarget.id}
          onCancel={() => setCancelTarget(null)}
          onConfirm={doCancel}
        />
      )}
    </div>
  );
}
